import React from 'react';

import waveLengthRange from '@client/config/waveLengthRange';
import { UxContext } from '@client/context/UxContext';
import i18n from '@client/utils/i18n';
import { CsvDataPoint, SpectrogramPoint } from '@ledium/types';
import { Publish } from '@mui/icons-material';
import { Button } from '@mui/material';
import { parse } from 'csv-parse/browser/esm';

interface SpectrogramRanges {
    pfd: Record<string, number>;
    irradiance: Record<string, number>;
}

export interface SpectrogramUploadResult {
    rawCsv: CsvDataPoint[];
    spectrogram: SpectrogramPoint[];
    ranges: SpectrogramRanges;
}

interface SpectrogramCsvUploadProps {
    onUpload: (data: SpectrogramUploadResult) => void;
}

type CsvRow = number[];

const csvParsePromise = (fileData: string, delimiter: string): Promise<string[][]> =>
    new Promise((resolve, reject) => {
        parse(fileData, { delimiter, trim: true }, (err, data) => {
            if (err) {
                reject(err);
            }
            resolve(data as string[][]);
        });
    });

const csvFix = (parsedCsv: string[][]): CsvRow[] | false => {
    if (!parsedCsv.length) {
        return false;
    }

    const csv = parsedCsv
        .map((parsedRow) => {
            const row = parsedRow.map((cell) => parseFloat(cell.replace(',', '.')));
            return row.length === 2 && row[0] ? row : null;
        })
        .filter((e): e is CsvRow => e !== null && e.length > 0);
    return csv.length ? csv : false;
};

const calculateMissingWaveLengthData = (csvData: CsvDataPoint[]): Array<{ waveLength: number; irradiance: number }> => {
    const wavelengths = [...csvData];
    const fullSpectrumData: Array<{ waveLength: number; irradiance: number }> = [];
    // reason: csvData is non-empty — checked by caller before calling this function
    let currWaveLength: CsvDataPoint = wavelengths.shift()!;
    let nextWaveLength: CsvDataPoint | undefined;
    let irradianceIncrement: number | null = 0;
    let lastFoundWaveLength: number = waveLengthRange.min;

    if (currWaveLength.waveLength === waveLengthRange.min) {
        nextWaveLength = wavelengths.shift();
    } else {
        nextWaveLength = currWaveLength;
        currWaveLength = {
            waveLength: waveLengthRange.min,
            value: 0
        };
    }

    irradianceIncrement = 0;
    lastFoundWaveLength = waveLengthRange.min;

    for (let w = waveLengthRange.min; w <= waveLengthRange.max; w++) {
        let irradiance: number;
        const foundInCsv = csvData.find((e) => e.waveLength === w);

        if (foundInCsv) {
            // reason: nextWaveLength is always defined when foundInCsv is truthy — it only becomes
            // undefined after all csv points are consumed, and we guard against that below
            if (w === nextWaveLength!.waveLength) {
                currWaveLength = nextWaveLength!;
                nextWaveLength = wavelengths.shift();
                if (nextWaveLength === undefined) {
                    nextWaveLength = {
                        waveLength: waveLengthRange.max,
                        value: 0
                    };
                    irradianceIncrement = null;
                } else {
                    irradianceIncrement = (nextWaveLength.value - currWaveLength.value) / (nextWaveLength.waveLength - currWaveLength.waveLength);
                }
            }

            irradiance = foundInCsv.value;
            lastFoundWaveLength = w;
        } else {
            irradiance = irradianceIncrement === null ? 0 : currWaveLength.value + irradianceIncrement * (w - lastFoundWaveLength);
        }
        fullSpectrumData.push({
            waveLength: w,
            irradiance
        });
    }

    return fullSpectrumData;
};

const calculateUploadedSpectrum = (csvData: CsvDataPoint[]): SpectrogramUploadResult => {
    const fullSpectrumData = calculateMissingWaveLengthData(csvData);

    // calculate pfd from irradiance
    const spectrogram: SpectrogramPoint[] = fullSpectrumData.map((e) => ({
        waveLength: e.waveLength,
        irradiance: e.irradiance,
        pfd: (e.waveLength * e.irradiance * 0.835954335468985) / 100
    }));

    // calculate pfd and irradiance areas for wavelength ranges
    const rangesEmpty: SpectrogramRanges = {
        pfd: {},
        irradiance: {}
    };
    waveLengthRange.ranges.forEach((e) => {
        rangesEmpty.pfd[e.name] = 0;
        rangesEmpty.irradiance[e.name] = 0;
    });
    const ranges = spectrogram.reduce((a, c) => {
        const range = waveLengthRange.ranges.filter((e) => c.waveLength >= e.from && c.waveLength <= e.to);
        range.forEach((r) => {
            a.pfd[r.name] += c.pfd;
            a.irradiance[r.name] += c.irradiance;
        });
        return a;
    }, rangesEmpty);
    for (const i in ranges.pfd) {
        ranges.pfd[i] = Math.round(ranges.pfd[i]);
        ranges.irradiance[i] = Math.round(ranges.irradiance[i]);
    }

    const filteredSpectrumData = spectrogram.filter((e, i) => !(i % waveLengthRange.step));

    return {
        rawCsv: csvData,
        spectrogram: filteredSpectrumData,
        ranges
    };
};

const parseCsv = async (fileData: string): Promise<SpectrogramUploadResult | false> => {
    let parsedData = await csvParsePromise(fileData, ';');
    let rawData = csvFix(parsedData);
    if (!rawData) {
        parsedData = await csvParsePromise(fileData, ',');
        rawData = csvFix(parsedData);
    }

    if (!rawData) {
        return false;
    }

    const spectrogram = rawData
        .map((csvLine) => {
            if (Number.isInteger(csvLine[0])) {
                return {
                    waveLength: csvLine[0],
                    value: csvLine[1]
                };
            }
            return null;
        })
        .filter((e): e is CsvDataPoint => e !== null);

    return spectrogram.length ? calculateUploadedSpectrum(spectrogram) : false;
};

const SpectrogramCsvUpload = ({ onUpload }: SpectrogramCsvUploadProps) => {
    // reason: SpectrogramCsvUpload is always rendered inside UxProvider
    const { handleSnackbar } = React.useContext(UxContext)!;

    function onCsvUpload({ target }: React.ChangeEvent<HTMLInputElement>) {
        const fileReader = new FileReader();
        fileReader.readAsText(target.files![0]);
        fileReader.onload = async () => {
            const spectrogram = await parseCsv(fileReader.result as string);
            if (spectrogram) {
                onUpload(spectrogram);
                handleSnackbar({
                    message: i18n('csv_file_is_successfully_uploaded') as string
                });
            } else {
                handleSnackbar({
                    message: i18n('could_not_parse_spectrogram_data_in_the_uploaded_CSV_file') as string,
                    severity: 'error'
                });
            }
        };
        target.value = '';
    }

    return (
        <>
            <input accept=".csv" style={{ display: 'none' }} id="raised-button-file" multiple type="file" onChange={onCsvUpload} />
            <label htmlFor="raised-button-file">
                <Button variant="contained" component="span" startIcon={<Publish />}>
                    {i18n('upload_csv_file')}
                </Button>
            </label>
        </>
    );
};

export default SpectrogramCsvUpload;
